import { defineStore } from 'pinia'
import axios from 'axios'

// APIのベースURLを環境変数から取得（なければ空文字列で相対パス）
const API_BASE_URL = process.env.VUE_APP_API_URL || ''

let interceptorId = null
let _router = null
let _initPromise = null
let _refreshPromise = null

// ルーターを外部から注入（auth.js ↔ router/index.js の循環参照を回避）
export function setRouter(router) {
  _router = router
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
    isLoading: false,
    sessionExpired: false
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken,
    isPremium: (state) => state.user?.is_premium_active || false
  },

  actions: {
    async login(credentials) {
      this.isLoading = true
      this.sessionExpired = false
      try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/login/`, credentials)

        if (!response.data.access || !response.data.refresh) {
          throw new Error('認証トークンが取得できませんでした')
        }

        this.accessToken = response.data.access
        this.refreshToken = response.data.refresh
        this.user = response.data.user

        localStorage.setItem('accessToken', this.accessToken)
        localStorage.setItem('refreshToken', this.refreshToken)

        this.setupAxiosInterceptors()

        return response.data
      } catch (error) {
        this.isLoading = false
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async register(userData) {
      this.isLoading = true
      try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/register/`, userData)
        return response.data
      } catch (error) {
        throw error
      } finally {
        this.isLoading = false
      }
    },

    logout() {
      this.user = null
      this.accessToken = null
      this.refreshToken = null
      _initPromise = null
      _refreshPromise = null

      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')

      delete axios.defaults.headers.common['Authorization']

      // インターセプターをクリア
      if (interceptorId !== null) {
        axios.interceptors.response.eject(interceptorId)
        interceptorId = null
      }
    },

    async refreshAccessToken() {
      // 既にリフレッシュ中なら、その完了を待つ（並行リクエスト競合を防止）
      if (_refreshPromise) {
        return await _refreshPromise
      }

      _refreshPromise = (async () => {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
            refresh: this.refreshToken
          })

          this.accessToken = response.data.access
          localStorage.setItem('accessToken', this.accessToken)

          return response.data.access
        } finally {
          _refreshPromise = null
        }
      })()

      return await _refreshPromise
    },

    setupAxiosInterceptors() {
      // 既存のインターセプターをクリア
      if (interceptorId !== null) {
        axios.interceptors.response.eject(interceptorId)
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${this.accessToken}`

      interceptorId = axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const requestUrl = error.config?.url || ''
          // トークンリフレッシュ自体の401は再試行しない（無限ループ防止）
          if (requestUrl.includes('/api/token/refresh/')) {
            return Promise.reject(error)
          }
          // 401エラーかつリフレッシュトークンがある場合のみリトライ
          if (error.response?.status === 401 && this.refreshToken && !error.config._retry) {
            error.config._retry = true
            try {
              await this.refreshAccessToken()
              error.config.headers['Authorization'] = `Bearer ${this.accessToken}`
              return axios.request(error.config)
            } catch (refreshError) {
              this.sessionExpired = true
              this.logout()
              // ルーター経由でSPA内遷移（ハードリロードを避ける）
              if (_router) {
                const currentPath = _router.currentRoute?.value?.fullPath
                _router.push({
                  name: 'Login',
                  query: {
                    redirect: currentPath,
                    expired: '1'
                  }
                })
              }
              return Promise.reject(refreshError)
            }
          }
          return Promise.reject(error)
        }
      )
    },

    async initializeAuth() {
      // 既に初期化中なら、その完了を待つ（再入防止）
      if (_initPromise) {
        return await _initPromise
      }

      if (!this.accessToken) {
        return
      }

      _initPromise = (async () => {
        this.setupAxiosInterceptors()
        try {
          const response = await axios.get(`${API_BASE_URL}/api/auth/profile/`)
          this.user = response.data
        } catch (error) {
          // インターセプター経由でlogoutが呼ばれていない場合のフォールバック
          if (this.accessToken) {
            this.logout()
          }
        }
      })()

      return await _initPromise
    }
  }
})
